import { Effect } from 'effect';

import type { PgClientLike, PgPoolLike } from './pg-types.js';
import {
  defineEffectSqlRunner,
  makeSqlRunnerError,
  type DefinedEffectSqlRunner,
  type EffectSqlTx,
  type SqlParams,
  type SqlRunnerError,
  type SqlRunnerOperation,
} from './types.js';

export type { PgClientLike, PgPoolLike, PgQueryResult } from './pg-types.js';

function attemptPromise<A>(
  operation: SqlRunnerOperation,
  evaluate: () => Promise<A>,
): Effect.Effect<A, SqlRunnerError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => makeSqlRunnerError(operation, cause),
  });
}

function attemptSync<A>(
  operation: SqlRunnerOperation,
  evaluate: () => A,
): Effect.Effect<A, SqlRunnerError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => makeSqlRunnerError(operation, cause),
  });
}

function transactionQuery(client: PgClientLike): EffectSqlTx {
  return {
    queryEffect: <T = Record<string, unknown>>(text: string, params: SqlParams = []) =>
      attemptPromise('transaction-query', async () => {
        const result = await client.query(text, [...params]);
        return result.rows as T[];
      }),
  };
}

/**
 * Wrap a `pg` (or compatible) Pool in one native runner while preserving the
 * Flue Promise shape. A transaction acquires one client, rolls back every
 * failed exit best-effort, and releases the client unconditionally.
 */
export function sqlRunnerFromPgPool(pool: PgPoolLike): DefinedEffectSqlRunner {
  return defineEffectSqlRunner({
    queryEffect: <T = Record<string, unknown>>(text: string, params: SqlParams = []) =>
      attemptPromise('query', async () => {
        const result = await pool.query(text, [...params]);
        return result.rows as T[];
      }),
    transactionEffect: <A, E, R>(use: (transaction: EffectSqlTx) => Effect.Effect<A, E, R>) =>
      Effect.acquireUseRelease(
        attemptPromise('connect', () => pool.connect()),
        (client) =>
          Effect.gen(function* () {
            yield* attemptPromise('begin', () => client.query('BEGIN'));
            const result = yield* use(transactionQuery(client));
            yield* attemptPromise('commit', () => client.query('COMMIT'));
            return result;
          }).pipe(
            Effect.onError(() =>
              attemptPromise('rollback', () => client.query('ROLLBACK')).pipe(
                Effect.catch(() => Effect.void),
              ),
            ),
          ),
        (client) => attemptSync('release', () => client.release()),
      ),
    closeEffect: () => attemptPromise('close', () => pool.end()),
  });
}
